import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';
import type { ZodValidationIssue } from '@trading/shared';
import { translateZodIssue } from './validation';

/**
 * Extract and translate API validation error details.
 *
 * The API error middleware emits structured errors of the form:
 *   { success: false, error: { code: string, message: string, details?: Record<string, ZodValidationIssue> } }
 *
 * Each `details` value is a structured `ZodValidationIssue` object (e.g.
 * `{ code: 'too_small', minimum: 1, type: 'string', inclusive: true }`).
 * This utility translates each issue to a human-readable string via i18n.
 *
 * Usage:
 *   const fieldErrors = getApiValidationErrors(err, t);
 *   // { exitDate: 'Exit date must be after entry date' }
 */
export function getApiValidationErrors(
  error: unknown,
  t: TFunction,
): Record<string, string> {
  if (!isAxiosError(error)) return {};

  const details: unknown = error.response?.data?.error?.details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};

  const result: Record<string, string> = {};
  for (const [field, value] of Object.entries(details as Record<string, unknown>)) {
    if (typeof value === 'object' && value !== null && 'code' in value) {
      result[field] = translateZodIssue(value as ZodValidationIssue, t);
    }
  }
  return result;
}

/**
 * Extract a human-readable error message from an unknown thrown value.
 *
 * Priority:
 *  1. Axios response body — `response.data.message` or `response.data.error.message`
 *  2. Standard Error message
 *  3. Provided fallback string
 *
 * Use this in Zustand store catch blocks instead of the common
 * `error instanceof Error ? error.message : fallback` pattern, so that
 * backend validation messages surface to the user rather than the generic
 * axios "Request failed with status code 4xx" string.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as Record<string, unknown> | undefined;
    const serverMsg =
      // Flat shape: { message: "..." } — used by some third-party/proxy responses
      (body?.message as string | undefined) ??
      // Our API error envelope: { error: { message: "..." } }
      ((body?.error as Record<string, unknown> | undefined)?.message as string | undefined);
    if (typeof serverMsg === 'string' && serverMsg.length > 0) return serverMsg;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

/**
 * Return the first translated validation error message from an API error,
 * or null if the error carries no structured details.
 *
 * Useful for surfaces that show a single error string (e.g. a toast) rather
 * than per-field inline errors.
 */
export function getFirstApiValidationError(
  error: unknown,
  t: TFunction,
): string | null {
  const errors = getApiValidationErrors(error, t);
  const first = Object.values(errors)[0];
  return first ?? null;
}
